import { Pipe, PipeTransform } from '@angular/core';
import moment, { Moment } from 'moment';
import { tryParseToMoment } from './format-datetime.pipe';
import { formatRelativeDatetime } from './format-relative-datetime.pipe';

// `m` formatted in Thai — moment's 'th' locale, registered in main.ts and
// applied per value — with its year in the Buddhist era: the year tokens
// (YYYY, YY) outside [brackets] are replaced by the era year as literal
// text, and moment formats the rest. Long-date tokens (LL, LLLL, …) are not
// rewritten, so spell the year out in the format.
//
// The year is formatted, not added to the date: `.add(543, 'year')` turns
// every 29 February into the 28th, because year + 543 is never a leap year —
// which printed 28 February on documents dated the 29th.
export const formatBuddhistEra = (m: Moment, format: string): string => {
    const year = String(m.year() + 543);
    const withEra = format.replace(
        /(\[[^\]]*\])|YYYY|YY/g,
        (token: string, literal: string | undefined) =>
            literal ?? `[${token === 'YYYY' ? year : year.slice(-2)}]`,
    );
    return m.clone().locale('th').format(withEra);
};

// A date and time in Thai with a Buddhist-era year: "22 ก.ย. 2569 14:30", or
// with `short` "22 ก.ย. 69 14:30" for grid cells. Unparseable input gives ''.
export const thaiDateTime = (value: unknown, short = false): string => {
    const parsed = tryParseToMoment(value);
    if (!moment.isMoment(parsed) || !parsed.isValid()) {
        return '';
    }
    return formatBuddhistEra(
        parsed,
        short ? 'D MMM YY HH:mm' : 'D MMM YYYY HH:mm',
    );
};

// `value | thaiDateTime` for the full form, `value | thaiDateTime: true` for
// the compact one.
@Pipe({ name: 'thaiDateTime' })
export class ThaiDateTimePipe implements PipeTransform {
    transform(value: unknown, short = false): string {
        return thaiDateTime(value, short);
    }
}

// For grids that switch between the two: the real date (compact unless
// `short` is false) when `absolute`, else relative time in Thai
// ("3 วันที่แล้ว"). A pure pipe, so each cell is formatted once per value and
// mode rather than on every change-detection pass.
@Pipe({ name: 'thaiDateOrRelative' })
export class ThaiDateOrRelativePipe implements PipeTransform {
    transform(value: unknown, absolute: boolean, short = true): string {
        return absolute
            ? thaiDateTime(value, short)
            : String(formatRelativeDatetime(value, 'th') ?? '');
    }
}
